import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { JackpotView } from "@/campaigns/modules/jackpot/types";
import {
  msToDeadline,
  msToNextRound,
  slotLengthMs,
  timedTier,
} from "@/campaigns/modules/jackpot/types";
import { cn } from "@/lib/utils";

import { countdownLabel } from "./countdown-label";

/**
 * The timed jackpot's secondary element: a tinted pill under the pot with a small ring that
 * drains and "Must pay within 2h 15m" — the same sentence shape as the pays-before pot's
 * "Must pay before €500", so the two kinds read as one family. Green while the round runs, the prize gold under five minutes, the
 * urgent orange inside the final minute — the pill carries the state on its own, so the colour
 * tells you where you are before you read it. In cooldown (won, next slot not started) the ring
 * goes, the colour goes grey, and it reads "Next round in 12m". Past the deadline and still unpaid
 * (`mustWin`) there is nothing left to count: the pill turns urgent orange, breathes, and says
 * what the overlay says in the same moment — "Must Drop Now" (its `mustDropNow` key, reused so both
 * surfaces read alike).
 *
 * Under the number, never beside it: every kind's secondary element sits in the same place.
 */
export const JackpotTimePill = ({
  view,
  now,
  cooldown = false,
  mustWin = false,
  compact = false,
  className,
}: {
  view: JackpotView;
  now: number;
  cooldown?: boolean;
  mustWin?: boolean;
  /** The compact card: type and padding match the pays-before line beside it on the board, so
   *  the two kinds' secondary lines read at one size. */
  compact?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const sizing = compact
    ? "px-3 py-[5px] text-[13px] leading-4"
    : "px-4 py-[7px] text-sm leading-[18px]";

  if (mustWin) {
    return (
      <motion.span
        className={cn(
          "inline-flex items-center gap-2 rounded-full bg-urgent/15 font-heading font-bold text-urgent",
          sizing,
          className
        )}
        animate={reducedMotion ? undefined : { opacity: [1, 0.55, 1] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
      >
        <span aria-hidden className="size-2 rounded-full bg-urgent" />
        {t("jackpot.mustDropNow", "Must Drop Now")}
      </motion.span>
    );
  }

  if (cooldown) {
    const wait = msToNextRound(view, now);
    if (wait === null) return null;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-2 rounded-full bg-muted-foreground/15 font-heading font-bold text-muted-foreground",
          sizing,
          className
        )}
      >
        {t("jackpot.nextRoundIn", "Next round in {{time}}", {
          time: countdownLabel(wait, t),
        })}
      </span>
    );
  }

  const left = msToDeadline(view, now);
  const slot = slotLengthMs(view);
  if (left === null) return null;

  const tier = timedTier(left);
  const colour =
    tier === "final"
      ? "var(--urgent)"
      : tier === "soon"
        ? "var(--prizes)"
        : "var(--active)";
  const remaining =
    slot && slot > 0 ? Math.min(1, Math.max(0, left / slot)) : 0;

  // A 22px ring: r = 8 → circumference ≈ 50.27. Dash = the share of the slot still ahead.
  const R = 8;
  const C = 2 * Math.PI * R;

  return (
    <span
      data-tier={tier}
      className={cn(
        "inline-flex items-center gap-[9px] rounded-full font-heading font-bold",
        compact
          ? "py-[5px] pr-3 pl-2 text-[13px] leading-4"
          : "py-[7px] pr-4 pl-[10px] text-sm leading-[18px]",
        className
      )}
      style={{
        color: colour,
        backgroundColor: `color-mix(in srgb, ${colour} 14%, transparent)`,
      }}
    >
      <svg
        aria-hidden
        width="22"
        height="22"
        viewBox="0 0 22 22"
        className="shrink-0 -rotate-90"
      >
        <circle
          cx="11"
          cy="11"
          r={R}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={4}
        />
        <circle
          cx="11"
          cy="11"
          r={R}
          fill="none"
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={`${(C * remaining).toFixed(2)} ${C.toFixed(2)}`}
        />
      </svg>
      {t("jackpot.mustPayWithin", "Must pay within {{time}}", {
        time: countdownLabel(left, t),
      })}
    </span>
  );
};
